import React, { useEffect, useMemo, useReducer } from "react";
import U2000DetailTable from "../../../../../u2000Core/components/tables/detail/U2000DetailTable";
import { useTranslation } from "react-i18next";
import U2000ActionBtn from "../../../../../u2000Core/components/buttons/U2000ActionBtn";
import { FaPlus, FaRegFile, FaTrashAlt } from "react-icons/fa";
import U2000ResponsiveToolbar from "../../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";

import { useU2000AuthContext } from "../../../../../u2000Core/context/auth/U2000UseAuthContext";
import { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { TFunction } from "i18next";
import { U2000IdColumnDef, type U2000TableColumn } from "../../../../../u2000Core/components/tables/basic/U2000BasicTableList";
import u2000FormState, { U2000FormStateEnumState, type U2000FormState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000StringArrayDisplay } from "../../../../../u2000Core/components/tables/display/U2000StringArrayDisplay";
import useU2000AxiosAuthenticated from "../../../../../u2000Core/axios/U2000UseAxiosAuthenticated";
import { u2000IntFormState, useU2000FormStateHandlers, type U2000FormSBusinessHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { NS, U3148ROLES } from "../../../../U3148";
import { emptyLogicalUnit, type U3148LogicalUnit, type U3148RoleLogicalUnit } from "../../../../U3148Service";
import { u3148RoleActionHandler } from "./U3148RoleActionHandlers";
import U3148RoleActionEditDlg from "./U3148RoleActionEditDlg";
import U3148RoleActionDeleteDlg from "./U3148RoleActionDeleteDlg";
import type { U3148RoleFormHandlers } from "../../../U3148RoleForm";

// Typage des props du composant
interface U3148RoleActionsComponentProps {
  u3148RoleFormStateExtendedHandlers: U3148RoleFormHandlers;
}

// Définition des colonnes pour le tableau des actions d'une unité logique
function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    U2000IdColumnDef,
    {
      name: "logicalUnitCode",
      label: t("role.lblLogicalUnitCode", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      widthClass: "w-[15%]",
      type: "string",
      align: "left",
    },
    {
      name: "logicalUnitDescription",
      label: t("role.lblLogicalUnitDescription", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[27%]",
      type: "string",
      align: "left",
    },
    {
      name: "actions",
      label: t("role.lblActions", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[60%]",
      type: "string",
      align: "left",
      display: (column, value, row) => {
        return <U2000StringArrayDisplay column={column} value={value} row={row} />;
      },
    },
  ];
}

// Composant principal pour la gestion des actions d'une unité logique
const U3148RoleActionsBody: React.FC<U3148RoleActionsComponentProps> = ({ u3148RoleFormStateExtendedHandlers }) => {
  // Translation
  const { t } = useTranslation();
  const { hasRole } = useU2000AuthContext();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);

  const gridStateHandlers = useU2000GridStateHandlers<U3148RoleLogicalUnit>();

  const validateRoleAction = (logicalUnit: U3148LogicalUnit) => {
    const result: Record<string, string> = {};
    if (logicalUnit.logicalUnitCode.trim() === "") {
      result["logicalUnitCode"] = "Logical Unit Code is required.";
    }
    return result;
  };

  // Business logic pour les actions d'une unité logique
  const u3148RoleActionBL: U2000FormSBusinessHandlers<U3148LogicalUnit> = {
    newRecord: () => {
      return emptyLogicalUnit;
    },

    // validation du formulaire
    validateForm: validateRoleAction,
  };

  // Handlers pour les actions d'une unité logique
  const u3148RoleActionformStateHandlers = useU2000FormStateHandlers<U3148LogicalUnit>({
    formState,
    formStateDispatch,
    businessHandlers: u3148RoleActionBL,
  });

  // Handlers spécifiques pour les actions d'une unité logique
  const u3148RoleActionHandlers = u3148RoleActionHandler({
    u2000AxiosAuthenticated,
    gridStateHandlers,
    t,
    formStateHandlers: u3148RoleActionformStateHandlers,
  });

  const columns = useMemo(() => getColumnsDef(t), [t]);

  // Transposer les données du formStateHandlers au gridStateHandlers pour LogicalUnitActions
  useEffect(() => {
    // Initialiser les colonnes et les données du tableau
    const currentColumns = gridStateHandlers.getColumns?.() ?? [];
    if (JSON.stringify(currentColumns) !== JSON.stringify(columns)) {
      gridStateHandlers.setSize(9999);
      gridStateHandlers.setColumns(columns);
      gridStateHandlers.setSelectedFields(columns.filter((col) => col.isDefaultColumn).map((col) => col.name));
      gridStateHandlers.setSelectedSortedFields(columns.filter((col) => col.isDefaultSortedColumn).map((col) => ({ name: col.name, sortOrder: "ASC" })));
    }

    // Initialiser les données du tableau
    const logicalUnitActions = u3148RoleFormStateExtendedHandlers.getData?.()?.actions as U3148RoleLogicalUnit[];
    gridStateHandlers.setData(Array.isArray(logicalUnitActions) ? logicalUnitActions : []);
    gridStateHandlers.setTotalRows(Array.isArray(logicalUnitActions) ? logicalUnitActions.length : 0);

    // Gérer les erreurs spécifiques au champ LogicalUnitActions
    const errorsField = u3148RoleFormStateExtendedHandlers.getOtherErrors()?.roleActions;
    if (errorsField !== undefined) {
      gridStateHandlers.setErrors(Array.isArray(errorsField) ? errorsField : errorsField ? [errorsField] : []);
    } else {
      gridStateHandlers.setErrors([]);
    }
  }, [u3148RoleFormStateExtendedHandlers.getData(), t]);

  // Mettre à jour le formStateHandlers parent lorsque les données du gridStateHandlers changent
  useEffect(() => {
    if (u3148RoleActionHandlers.dataChangedEvent === 0) return;

    const logicalUnitActions = gridStateHandlers.getData();
    u3148RoleFormStateExtendedHandlers.setData({
      ...u3148RoleFormStateExtendedHandlers.getData?.(),
      actions: logicalUnitActions,
    });
  }, [u3148RoleActionHandlers.dataChangedEvent]);

  // Désactiver le bouton de modification si aucun ou plus d'une ligne sélectionnée
  function disabledUpdateBtn(): boolean {
    const selectedRows = gridStateHandlers.getSelectedRows();
    return u3148RoleFormStateExtendedHandlers.getFormState?.() === U2000FormStateEnumState.READ || selectedRows.length !== 1 ? true : false;
  }

  // Désactiver le bouton de suppression si aucune ligne sélectionnée
  function disableDeleteBtn(): boolean {
    const selectedRows = gridStateHandlers.getSelectedRows();
    return u3148RoleFormStateExtendedHandlers.getFormState?.() === U2000FormStateEnumState.READ || selectedRows.length === 0 ? true : false;
  }

  // Préparer l'array d'actions pour le toolbar responsive
  const actions = React.useMemo(
    () => [
      {
        key: "add",
        label: t("btnAdd", { ns: NS }),
        handleClick: u3148RoleActionHandlers.onAdd,
        hasRole: hasRole(U3148ROLES.ROLES),
        disabled: u3148RoleFormStateExtendedHandlers.getFormState?.() === U2000FormStateEnumState.READ,
        icon: FaPlus,
        Component: U2000ActionBtn,
      },
      {
        key: "modify",
        label: t("btnModify", { ns: NS }),
        handleClick: u3148RoleActionHandlers.onModify,
        hasRole: hasRole(U3148ROLES.ROLES),
        disabled: disabledUpdateBtn(),
        icon: FaRegFile,
        Component: U2000ActionBtn,
      },
      {
        key: "delete",
        label: t("btnDelete", { ns: NS }),
        handleClick: u3148RoleActionHandlers.onDelete,
        hasRole: hasRole(U3148ROLES.ROLES),
        disabled: disableDeleteBtn(),
        icon: FaTrashAlt,
        Component: U2000ActionBtn,
      },
    ],
    [t, u3148RoleActionHandlers]
  );

  // Render
  return (
    <>
      <U3148RoleActionEditDlg u3148RoleActionHandler={u3148RoleActionHandlers} t={t} NS={NS} />
      <U3148RoleActionDeleteDlg u3148RoleActionHandler={u3148RoleActionHandlers} t={t} NS={NS} />

      <div className="mt-1 mb-1 flex items-center gap-x-2 border-b-2 pb-3">
        <U2000ResponsiveToolbar actions={actions} />
      </div>
      <U2000DetailTable
        gridStateHandlers={gridStateHandlers}
        allowRowSelection={u3148RoleFormStateExtendedHandlers.getFormState?.() === U2000FormStateEnumState.EDIT}
        label={t("role.lblRoleAction", {
          count: u3148RoleActionHandlers.gridStateHandlers.getTotalRows(),
          ns: NS,
        })}
      />
    </>
  );
};

export default U3148RoleActionsBody;
